import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ViewChildren, QueryList, ElementRef, Input, Output, EventEmitter, PLATFORM_ID, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterModule, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { trigger, transition, style, animate, state } from '@angular/animations';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { BreakpointObserver } from '@angular/cdk/layout';

import { RecommendationService } from '../../../../core/services/recommendation.service';
import { environment } from '../../../../../environments/environment';
import { Utils } from '../../../../shared/utils';
import { KebabComponent } from '../../../../layout/designs/kebab-component/kebab-component';
import { YoutubePlayerService } from '../../../../core/services/youtube.service';
import { TeaserTrailerService } from '../../../../core/services/trailers.service';
import { LanguageService } from '../../../../core/services/language/language.service';

@Component({
  selector: 'app-movie-recommendations',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    KebabComponent
  ],
  templateUrl: './movie-recommendations.html',
  styleUrl: './movie-recommendations.scss',
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('500ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ]),
    trigger('fadeAndToggle', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('500ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      state('active', style({ transform: 'scale(1)', background: 'linear-gradient(340deg, #96f2d7, #96f2d7, #38d9a9, #12b886)', borderRadius: '2.5em' })),
      state('inactive', style({ transform: 'scale(1)', backgroundColor: '#f8f9fa', borderRadius: '2.5em' })),
      transition('inactive => active', [
        animate('200ms cubic-bezier(.68,-0.55,.27,1.55)')
      ]),
      transition('active => inactive', [
        animate('200ms ease-out')
      ])
    ])
  ]
})
export class MovieRecommendations implements OnInit, AfterViewInit {

  @Input() showPlayer: boolean = false;
  @Output() showPlayerChange = new EventEmitter<boolean>();
  @ViewChild('trailerCardsContainer') trailerCardsContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('trailerContainer') trailerContainer!: ElementRef<HTMLDivElement>;
  @ViewChildren(KebabComponent) kebabs!: QueryList<KebabComponent>;

  movieId: number | null = null;
  movieName: string | null = null;
  recommendations: any[] = [];
  frontendUrl: string = environment.frontendUrl;
  backendUrl: string = environment.backendUrl;
  selectedItemIndex: number = 0;
  isActive: HTMLDivElement | null = null;
  kebabActive: boolean = true;
  activeCard: HTMLDivElement | null = null;
  activeCardIndex: number | null = null;
  readonly initialSkeletons = Array(4).fill(0);
  skeletonCount: number = 6;
  squareSkeletonTheme: any = {};
  lineSkeletonTheme: any = {};
  canScrollLeft: boolean = false;
  canScrollRight: boolean = true;
  trailerId: string = '';
  isLoading: boolean = true;

  private platformId = inject(PLATFORM_ID);
  private destroy$ = new Subject<void>();

  public readonly languageService = inject(LanguageService);

  constructor(
    private route: ActivatedRoute,
    private recommendationService: RecommendationService,
    private utils: Utils,
    private youtubeService: YoutubePlayerService,
    private breakpointObserver: BreakpointObserver,
    private cdr: ChangeDetectorRef,
    private trailerService: TeaserTrailerService
  ) {}

  ngOnInit(): void {
    this.updateSkeletonCount();
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const slug = params.get('slug');
      this.fetchRecommendations(slug);
    });

    this.languageService.currentLanguage$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.movieId) {
        this.fetchRecommendations();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  fetchRecommendations(slugParam?: string | null): void {
    const slug = slugParam !== undefined ? slugParam : this.route.snapshot.paramMap.get('slug');
    const [id, name] = slug ? slug.split('-') : [null, null];

    this.movieId = id ? parseInt(id, 10) : null;
    this.movieName = name ? decodeURIComponent(name.replace(/-/g, ' ')) : null;

    if (this.movieId) {
      this.isLoading = true;
      const lang = this.languageService.currentLanguage?.code || 'en-US';
      this.recommendationService.getRecommendations(this.movieId, 'movie', lang).subscribe({
        next: (recommendations) => {
          this.recommendations = recommendations?.results || [];
          this.isLoading = false;
          setTimeout(() => this.checkScroll(), 300);
        },
        error: () => {
          this.isLoading = false;
        }
      });
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => this.checkScroll(), 400);
    }
  }

  formattedMovieName(sentence: string): string {
    return this.utils.movieNameSplitter(sentence);
  }

  movieNameSplitter(movieName: string): string {
    return this.utils.movieNameSplitter(movieName);
  }

  getMediaRoute(movie: any): (string | number)[] {
    const type = movie?.media_type || (movie?.first_air_date ? 'tv' : 'movie');
    const title = movie?.title || movie?.original_title || movie?.name || movie?.original_name || 'details';
    const slug = `${movie.id}-${this.utils.movieNameSplitter(title) || 'details'}`;
    return ['/', type, slug];
  }

  onImageError(event: Event) {
    return this.utils.onImageVerticalError(event);
  }

  onKebabToggle(clickedKebab: KebabComponent, index: number) {
    this.kebabs.forEach(kebab => {
      if (kebab !== clickedKebab) kebab.isActive = false;
    });

    if (!clickedKebab.isActive) {
      this.activeCardIndex = null;
    } else {
      this.activeCardIndex = index;
    }
  }

  updateSkeletonCount() {
    if (!isPlatformBrowser(this.platformId)) return;
    const width = window.innerWidth;

    if (width < 768) {
      this.squareSkeletonTheme = {
        width: '85vw',
        height: '240px',
        borderRadius: '1rem',
        margin: '0 0.5rem'
      };
      this.skeletonCount = 3;
    } else if (width < 1024) {
      this.squareSkeletonTheme = {
        width: '360px',
        height: '203px',
        borderRadius: '1rem',
        margin: '0 0.75rem'
      };
      this.skeletonCount = 4;
    } else {
      this.squareSkeletonTheme = {
        width: '420px',
        height: '236px',
        borderRadius: '1rem',
        margin: '0 0.75rem'
      };
      this.skeletonCount = 3;
    }
  }

  onCardHover(index: number) {
    this.activeCardIndex = index;
    this.selectedItemIndex = index;
  }

  onCardLeave() {
    this.activeCardIndex = null;
  }

  scrollLeft() {
    if (!this.trailerCardsContainer) return;

    const container = this.trailerCardsContainer.nativeElement;
    const scrollAmount = this.getCardScrollAmount();

    container.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth'
    });

    this.updateScrollState();
  }

  scrollRight() {
    if (!this.trailerCardsContainer) return;

    const container = this.trailerCardsContainer.nativeElement;
    const scrollAmount = this.getCardScrollAmount();

    container.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });

    this.updateScrollState();
  }

  checkScroll() {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.trailerCardsContainer) return;

    const container = this.trailerCardsContainer.nativeElement;
    this.canScrollLeft = container.scrollLeft > 0;
    this.canScrollRight = container.scrollLeft < (container.scrollWidth - container.clientWidth - 10);
  }

  private updateScrollState() {
    if (!isPlatformBrowser(this.platformId)) return;
    requestAnimationFrame(() => this.checkScroll());
  }

  private getCardScrollAmount(): number {
    if (!isPlatformBrowser(this.platformId)) {
      return 400;
    }

    const container = this.trailerCardsContainer.nativeElement;
    const card = container.querySelector('.trailer-card-wrapper') as HTMLElement;

    if (!card) return 400;

    const gap = parseFloat(getComputedStyle(container).columnGap || '0');
    return card.offsetWidth + gap;
  }

  async playTrailer(movie: any): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const movieId = movie.id;
    const mediaType = movie.first_air_date ? 'tv' : 'movie';

    this.trailerService
      .getTeaserOrTrailers(parseInt(movieId), mediaType)
      .subscribe(trailers => {
        const trailer = trailers?.results?.find((t: any) =>
          t.type === 'Trailer' && (t.official === true)
        ) || trailers?.results?.find((t: any) => t.type === 'Trailer')
          || trailers?.results?.[0];

        if (trailer) {
          this.trailerId = trailer.key;

          // Emit to parent to show modal
          this.showPlayer = true;
          this.showPlayerChange.emit(true);

          // Prevent body scroll
          document.body.style.overflow = 'hidden';

          // Wait for modal to render, then find the element directly
          setTimeout(() => {
            const trailerElement = document.getElementById('trailer-player');
            if (trailerElement) {
              this.youtubeService.playTrailer(this.trailerId, 'trailer-player');
            }
          }, 400);
        } else {
          this.showPlayer = false;
          this.showPlayerChange.emit(false);
        }
      });
  }

  closeTrailer(): void {
    this.showPlayer = false;
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = '';
    }

    if (this.youtubeService) {
      this.youtubeService.destroyPlayer();
    }
  }
}
